import {
  ResponseStatus,
  type QuestionResponseDetails,
  type SubResponse,
  type SubQuestionResponseEntry,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
  type NaReasonOverride,
} from './preserveResponseFields';

/**
 * Mutation-input builders for PA-04 answer TOGGLE-OFF (clear).
 *
 * Both builders live in one module because they express the same
 * wire concept — "this answer no longer stands" — through the same
 * mechanism: the mutation is a full-PATCH, so a result is cleared
 * by OMITTING it from the payload (`ResponseResult` has no CLEAR
 * member; absence IS the clear). They differ only in scope:
 *
 *   - {@link buildAnswerClearUpdate} clears the PARENT answer and
 *     cascades the clear to every sub-question result + NA reason.
 *   - {@link buildSubQuestionAnswerClearUpdate} clears ONE
 *     sub-question's result + NA reason; siblings and the parent
 *     are untouched.
 *
 * Ground truth is desktop's `updateQuestionAnswer` in
 * `src/helper/performAuditHelpers.ts` (the `!result` branches):
 * parent clear → `responseResult: undefined` + every sub's
 * result/naReason/naReasonId undefined + parent naReason cleared;
 * sub clear → target-only. Comment, mediaIds, and taskIds survive
 * a clear on both paths (preservation via the shared
 * {@link buildPreservedResponseData} /
 * {@link projectSubResponsesForPreservation} helpers).
 *
 * Pure — no react, no hooks, no side effects.
 */

export interface BuildAnswerClearUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — sent as `respondedBy` AND `lastUpdatedBy`. */
  readonly alias: string;
  /** The question's current cached response. Comment / mediaIds /
   *  taskIds are preserved onto the outgoing payload; naReason and
   *  naReasonId are cleared (a cleared answer can't be NA). */
  readonly previous?: QuestionResponseDetails | undefined;
  /** Sub-question responses on the current question. Every entry is
   *  cascade-cleared: result + naReason + naReasonId dropped while
   *  comment / mediaIds / taskIds are preserved — matching desktop's
   *  "sub-questions don't retain answers when the parent is reset". */
  readonly subQuestionResponses?: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Builds the mutation input that clears the PARENT question's answer.
 *
 * Wire shape (V1 parity):
 *   - `responseResult` omitted — full-PATCH absence clears it.
 *   - `responseStatus: PENDING` — the question is back to unanswered.
 *   - parent `response`: comment / mediaIds / taskIds preserved;
 *     naReason + naReasonId cleared via `naReasonOverride: null`.
 *   - `subResponses`: every entry keeps comment / mediaIds / taskIds
 *     but loses result + naReason + naReasonId (cascade).
 */
export function buildAnswerClearUpdate(
  args: BuildAnswerClearUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus: ResponseStatus.PENDING,
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
  };

  const preservedResponse = buildPreservedResponseData(args.previous, {
    naReasonOverride: null,
  });
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  if (args.subQuestionResponses && args.subQuestionResponses.length > 0) {
    // Cascade: clear every sub's naReason during projection, then
    // drop every sub's result from the projected entries.
    const naReasonOverrides: Record<string, NaReasonOverride> = {};
    for (const entry of args.subQuestionResponses) {
      naReasonOverrides[entry.subQuestionId] = null;
    }
    const projected = projectSubResponsesForPreservation(args.subQuestionResponses, {
      naReasonOverrides,
    });
    input.subResponses = projected.map(omitResult);
  }

  return input;
}

export interface BuildSubQuestionAnswerClearUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  /** GID of the PARENT question — the endpoint routes sub saves on
   *  the parent id + the `subResponses` payload. */
  readonly questionId: string;
  /** Current user alias — sent as `respondedBy` AND `lastUpdatedBy`. */
  readonly alias: string;
  /** The sub-question whose answer is being cleared. */
  readonly subQuestionId: string;
  /** The parent question's cached response — preserved VERBATIM
   *  (including naReason: a sub clear doesn't touch the parent). */
  readonly previous?: QuestionResponseDetails | undefined;
  /** Existing sub-question responses. Every non-target entry is
   *  preserved as-is; the target loses result + naReason + naReasonId
   *  while keeping comment / mediaIds / taskIds. */
  readonly subQuestionResponses?: readonly SubQuestionResponseEntry[] | undefined;
  /** Total sub-questions on the parent. Present for signature parity
   *  with {@link buildSubQuestionAnswerUpdate}; a clear always yields
   *  `responseStatus: PENDING` because the target is unanswered and
   *  the parent is no longer complete. */
  readonly totalSubQuestions: number;
}

/**
 * Builds the mutation input that clears ONE sub-question's answer.
 *
 * Wire shape (V1 parity):
 *   - Top-level `responseResult` omitted (sub saves never set it).
 *   - `responseStatus: PENDING` — with the target's result dropped,
 *     the parent is incomplete, so the derivation that
 *     `buildSubQuestionAnswerUpdate` performs collapses to the
 *     PENDING constant here.
 *   - Target sub entry: result + naReason + naReasonId dropped;
 *     comment / mediaIds / taskIds preserved.
 *   - Sibling entries and the parent `response` block: preserved
 *     verbatim (per-sub clear is per-sub only).
 */
export function buildSubQuestionAnswerClearUpdate(
  args: BuildSubQuestionAnswerClearUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const naReasonOverrides: Record<string, NaReasonOverride> = {
    [args.subQuestionId]: null,
  };
  const projected = projectSubResponsesForPreservation(args.subQuestionResponses, {
    naReasonOverrides,
  });
  const subResponses = projected.map((entry) =>
    entry.subQuestionId === args.subQuestionId ? omitResult(entry) : entry,
  );

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus: ResponseStatus.PENDING,
    // Deliberately not setting top-level responseResult — see docblock.
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
    subResponses,
  };

  const preservedResponse = buildPreservedResponseData(args.previous);
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  return input;
}

/**
 * Immutably rebuild a projected sub entry without its `result`.
 * Full-PATCH semantics: an absent `result` on the wire clears the
 * sub's answer server-side while the other projected fields
 * (comment / mediaIds / taskIds) ride through.
 */
function omitResult(entry: SubResponse): SubResponse {
  const next: SubResponse = { ...entry };
  delete next.result;
  return next;
}
